import { observer, inject } from "mobx-react";
import * as React from "react";
import * as _ from "lodash";

import {Utils, Defaults} from "../../common/utils";
import {isActiveInactiveFilter} from "../../common/filters";

import { CommonStore, UserStore } from "../../stores";
import { MapType, MapTypeDataQuery, Mapping, Student, Committee, Course } from "Stores/commonStore";
import { User } from "Stores/userStore";

import {
    Column,
    ColumnDef,
    ColumnFiltersState,
    flexRender,
    getCoreRowModel,
    getFilteredRowModel,
    getSortedRowModel,
    PaginationState,
    SortingState,
    useReactTable
} from "@tanstack/react-table";
import {Alert, Button, Col, Form, Modal, Row, Spinner, Table} from "react-bootstrap";
import { CheckboxButton } from "../Common/CheckboxButton";
import { toJS } from "mobx";
import {useEffect, useState} from "react";
import DebouncedInput from "Components/Common/DebouncedInput";

interface MappingSelectorProps {
    commonStore?: CommonStore
    userStore?: UserStore
    faculty: User
    handleClose: () => void
    mapType?: Mapping
}

interface MappingSelectorState {
    selected: string[],
    selectedItems: MapType[] | undefined,
    shouldShowCurrent: boolean
    didUpdate: boolean
    showFilters: boolean
    isSaving: boolean,
    pages: number
    pageSize: number
    pageNumber: number
    loading: boolean
}

@inject("commonStore", "userStore")
@observer
export class UserMappingSelector extends React.Component<MappingSelectorProps, MappingSelectorState> {

    getRowId = (row: MapType) => {
        return (this.props.mapType && this.props.mapType === "students") ? row.ruuid : row.id;
    };

    getCurrentIds = (selectedItems: MapType[] | undefined) => {
        if(this.props.commonStore && this.props.faculty && this.props.mapType) {
            if(selectedItems){
                return selectedItems.map( (c: MapType) => this.getRowId(c) as string);
            }
            const selectedObjs: Array<MapType> | undefined = this.props.faculty[this.props.mapType];
            return selectedObjs ? selectedObjs.map( (c: MapType) => this.getRowId(c) as string) : [];
        } else {
            return [];
        }
    };

    state = {
        selected: this.getCurrentIds(undefined),
        selectedItems: this.props.userStore && this.props.userStore.user && toJS(this.props.userStore.user[this.props.mapType || "committees"]),
        shouldShowCurrent: true,
        didUpdate: false,
        showFilters: false,
        isSaving: false,
        pages: 0,
        pageSize: Defaults.tablePageSize(),
        pageNumber: Defaults.tablePage(),
        loading: false
    };

    filtering = false;

    unmounting = false;

    reactTableRefAll: any;

    reactTableRefCurrent: any;

    save = () => {
        this.setState({isSaving: true}, () => {
            if(!this.props.userStore || !this.props.userStore.user || !this.props.mapType || !this.state.selectedItems || !this.props.faculty || !this.props.commonStore){
                console.error("Can't save, because no faculty to save to");
                return;
            }

            this.resetCounts();
            const current = this.getCurrentIds(this.state.selectedItems);

            const added = _.differenceWith(this.state.selected, current, (valOne: string | number, valTwo: string | number) => valOne === valTwo);
            const removed = _.differenceWith(current, this.state.selected, (valOne: string | number, valTwo: string | number) => valOne === valTwo);

            Promise.resolve(this.props.userStore.addRemoveFacultyMappings(this.props.userStore.user, this.props.mapType, added, removed)).then(async() => {
                if(!this.unmounting){
                    let selectedItems = this.state.selectedItems;
                    if (this.props.userStore && this.props.userStore.user) {
                        const faculty = await this.props.userStore.fetchUserById((this.props.userStore.user.ruuid));
                        selectedItems = toJS(faculty[this.props.mapType || "committees"]);
                        return this.setState({selectedItems, isSaving: false, selected: this.getCurrentIds(selectedItems)});
                    }
                    this.setState({isSaving: false});
                }
            });
        });
    };

    resetCounts = () => {
        // these counts show up in the modal after save
        if(this.props.userStore){
            this.props.userStore.resetAddedMapCount();
            this.props.userStore.resetRemovedMapCount();
        }
    };

    resetFaculty = () => {
        if(this.props.userStore){
            this.props.userStore.resetUserById();
        }
    };

    exit = () => {
        this.resetCounts();
        this.resetFaculty();

        if(this.props.commonStore && this.props.mapType) {
            this.props.commonStore.resetFilters(this.props.mapType);
        }

        this.props.handleClose();
    };

    isRowSelected = (row: MapType): boolean => {
        const selectedIdx = this.state.selected.indexOf(this.getRowId(row) as string);
        return selectedIdx >= 0;
    };

    onSelect = (row: MapType): void => {
        const nextState = {
            selected: this.state.selected.slice()
        };
        if( !this.isRowSelected(row) ) {
            nextState.selected.push(this.getRowId(row) as string);
        } else {
            const idx = this.state.selected.indexOf(this.getRowId(row) as string);
            nextState.selected.splice(idx, 1);
        }
        this.setState(nextState);
    };


    studentTableColumns = (): ColumnDef<Student>[] => {
        return [{
            header: "First Name",
            accessorKey: "first_name",
            size: 100,
        }, {
            header: "Last Name",
            accessorKey: "last_name",
            size: 100,
        }, {
            header: "Student ID",
            accessorKey: "schools_student_id",
            size: 100,
        }, {
            header: "Email",
            accessorKey: "student_email",
            size: 150,
        }, {
            header: "Examsoft Status",
            id: "status",
            accessorKey: "status",
            filterFn: isActiveInactiveFilter
        }];
    };

    committeeTableColumns = (): ColumnDef<Committee>[] => {
        return [{
            header: "Name",
            accessorKey: "committee_name",
            size: 100,
        }];
    };

    courseTableColumns = (): ColumnDef<Course>[] => {
        return [{
            header: "Name",
            accessorKey: "name",
            size: 500,
        },{
            header: "Term",
            accessorKey: "term_name",
            size: 150,
        }];
    };

    typeSpecificTableColumns = (type: "students" | "courses" | "committees"): ColumnDef<MapType>[] => {
        switch (type) {
            case "students":
                return this.studentTableColumns();
            case "courses":
                return this.courseTableColumns();
            case "committees":
                return this.committeeTableColumns();
        }
    };

    deselectAll = () => {
        let displayed: string[];

        if(this.state.shouldShowCurrent){
            displayed = this.reactTableRefCurrent.getResolvedState().sortedData.map( ({_original}: {_original: MapType}) => this.getRowId(_original));

        } else {
            displayed = this.reactTableRefAll.getResolvedState().sortedData.map( ({_original}: {_original: MapType}) => this.getRowId(_original));
        }
        this.setState({selected: _.pull(this.state.selected.slice(), ...displayed)});
    };

    selectAll = () => {
        let displayed: string[];

        if(this.state.shouldShowCurrent){
            displayed = this.reactTableRefCurrent.getResolvedState().sortedData.map( ({_original}: {_original: MapType}) => this.getRowId(_original));
        } else {
            displayed = this.reactTableRefAll.getResolvedState().sortedData.map( ({_original}: {_original: MapType}) => this.getRowId(_original));

        }
        this.setState({selected: _.union(this.state.selected.slice(), displayed)});
    };

    getCheckboxHeader = () => {
        let allSelected: boolean;
        if(this.state.shouldShowCurrent){
            if(this.reactTableRefCurrent) {
                const displayed = this.reactTableRefCurrent.getResolvedState().sortedData.map(({_original}: { _original: MapType }) => this.getRowId(_original));
                allSelected = _.differenceWith(displayed, this.state.selected).length === 0;
            } else {
                const original = this.getCurrentIds(this.state.selectedItems);
                allSelected = _.differenceWith(original, this.state.selected).length === 0;
            }
        } else {
            if(this.reactTableRefAll){
                const displayed = this.reactTableRefAll.getResolvedState().sortedData.map( ({_original}: {_original: MapType}) => this.getRowId(_original));
                allSelected = _.differenceWith(displayed, this.state.selected).length === 0;
            } else {
                const commonItems = this.props.commonStore!["filteredSorted" + _.capitalize(this.props.mapType)].slice().map((obj: MapType) => this.getRowId(obj));
                allSelected = _.differenceWith(commonItems, this.state.selected).length === 0;
            }
        }
        return (
            <Button onClick={allSelected ? this.deselectAll : this.selectAll} className="add-remove-button">
                {allSelected ? <i className="icon icon-check-square"/> : <i className="icon icon-square-o"/>}
            </Button>);
    };

    getColumns = (type: "students" | "courses" | "committees"): ColumnDef<MapType>[] =>
        [
            {
                id: "selector",
                cell: (props) => (<CheckboxButton isSelected={this.isRowSelected(props.row.original)} onClick={this.onSelect} data={props.row.original} />),
                // To add the select all feature, uncomment this line
                // Header: this.getCheckboxHeader,
                size: 40,
            },
            ...this.typeSpecificTableColumns(type)
        ];

    componentWillUnmount() {
        this.unmounting = true;
    }

    componentDidMount () {
        if( !this.props.commonStore || !this.props.faculty) return;
        switch (this.props.mapType) {
            case "students":
                return this.props.commonStore.fetchStudents({pageSize: 100, page: 1});
            case "courses":
                return this.props.commonStore.fetchCourses("all", {pageSize: 100, page: 1, sortBy: "name"});
            case "committees":
                return this.props.commonStore.fetchCommittees();
        }
    }

    getHeader = () => {
        switch (this.props.mapType) {
            case "students":
                return "Mentoring";
            case "courses":
                return "Courses";
            case "committees":
                return "Committees";
        }
    };

    renderCurrent = () => {
        switch (this.props.mapType) {
            case "students":
                return "CURRENT STUDENTS";
            case "courses":
                return "CURRENT COURSES";
            case "committees":
                return "CURRENT COMMITTEES";
        }
    };

    renderAll = () => {
        switch (this.props.mapType) {
            case "students":
                return "ALL STUDENTS";
            case "courses":
                return "ALL COURSES";
            case "committees":
                return "ALL COMMITTEES";
        }
    };

    handleFilterDisplayToggle = () => {
        this.setState({ showFilters: !this.state.showFilters });
    };

    handleShowCurrent = () => {
        if(!this.state.shouldShowCurrent){
            this.setState({shouldShowCurrent: true});
        }
    };

    handleShowAll = () => {
        if(this.state.shouldShowCurrent){
            this.setState({shouldShowCurrent: false});
        }
    };

    handleModalDismiss = () => {
        this.props.handleClose();
    };

    handleMessageDismiss = () => {
        this.resetCounts();
    };

    renderTable = (mapType: Mapping) => {
        if (!this.props.commonStore || !this.props.mapType) return null;

        let data: MapType[];
        if (this.state.shouldShowCurrent) {
            data = this.state.selectedItems || [];
        } else {
            if(mapType === "students" || mapType === "courses") {
                data = this.props.commonStore[mapType] || [];
            } else {
                data = toJS(this.props.commonStore["filteredSorted" + _.capitalize(mapType)]) || [];
            }
        }

        if (this.props.commonStore[mapType]) {
            if ((mapType === "students" || mapType === "courses") && !this.state.shouldShowCurrent) {
                return <AllReactTable
                    columns={this.getColumns(mapType)}
                    commonStore={this.props.commonStore}
                    mapType={this.props.mapType}
                    showFilters={this.state.showFilters} />;
            } else {
                return <CurrentReactTable columns={this.getColumns(mapType)} data={data} showFilters={this.state.showFilters}/>;
            }
        } else {
            return (this.props.commonStore[mapType]
                ? <Alert>{`No ${mapType} available.`}</Alert>
                : <div style={{textAlign: "center", paddingTop: 50}}><Spinner className="green-spinner" animation={"border"} variant={"secondary"}/></div>);
        }

    };

    render() {
        if(!this.props.commonStore || !this.props.userStore || !this.props.mapType || !this.props.faculty) return null;

        return (
            <Modal centered scrollable show={true} onHide={this.handleModalDismiss} dialogClassName="modal-container wide mapping users">
                <Modal.Header>
                    <Row>
                        <Col md={12} lg={3} className="text-lg-left">
                            <span className="modal-title">{this.getHeader()}</span>
                        </Col>
                        <Col md={12} lg={9} className="actions text-lg-end">
                            <Button variant={"outline-light"} className={this.state.shouldShowCurrent ? "active" : ""}
                                    onClick={this.handleShowCurrent} >{this.renderCurrent()}</Button>
                            <Button variant={"outline-light"} className={!this.state.shouldShowCurrent ? "active" : ""}
                                    onClick={this.handleShowAll} >{ this.renderAll() }</Button>
                        </Col>
                    </Row>
                </Modal.Header>
                <Modal.Body className={"mapping-selector list-container" + (this.state.showFilters ? " show-filters" : "")}>
                    <Row className="section-action-bar auto-width">
                        <Col className="name text-left">{this.props.faculty.name}</Col>
                        <Col className="actions text-right">
                            <Button
                                title="Filter"
                                variant={"icon"}
                                onClick={this.handleFilterDisplayToggle}>
                                <i className="icon icon-filter" />
                            </Button>
                        </Col>
                    </Row>
                    { this.renderTable(this.props.mapType) }
                </Modal.Body>
                <Modal.Footer>
                    { this.state.isSaving || !this.props.userStore || this.props.userStore.isUpdatingMappings ?
                        <Alert className="inline-message">
                            {"Updating mappings."}
                        </Alert> : null }
                    { this.props.userStore && (this.props.userStore.newMappingsCount || this.props.userStore.removedMappingsCount) ?
                        <Alert className="inline-message"
                                    onClose={this.handleMessageDismiss} >
                            {`Added ${this.props.userStore.newMappingsCount || 0} mappings.
                            Removed ${this.props.userStore.removedMappingsCount || 0} mappings.`}
                        </Alert> : null }
                    <Button
                        onClick={this.exit}
                        variant="secondary"
                        size="lg">
                        Exit
                    </Button>
                    <Button
                        onClick={this.save}
                        variant="primary"
                        size="lg"
                        disabled={this.state.isSaving || this.props.userStore.isUpdatingMappings}>
                        {this.state.isSaving || this.props.userStore.isUpdatingMappings ? <Spinner animation={"border"} variant={"secondary"} className="green-spinner"/> : "Save"}</Button>
                </Modal.Footer>
            </Modal>
        );
    }
}

interface CurrentReactTableProps {
    columns: ColumnDef<MapType>[]
    data: MapType[]
    showFilters: boolean
}

function CurrentReactTable({columns, data, showFilters}: CurrentReactTableProps) {
    const table = useReactTable({
        columns,
        data,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
    });
    return (
        <div className={"list"}>
            <Table>
                <thead>
                {table.getHeaderGroups().map(headerGroup => (
                    <tr key={headerGroup.id}>
                        {headerGroup.headers.map(header => (
                            <th key={header.id}>
                                {header.isPlaceholder
                                    ? null
                                    : (
                                        <>
                                            <div
                                                className={header.column.getCanSort() ? "cursor-pointer " + (header.column.getIsSorted() || "") : ""}
                                                onClick={header.column.getToggleSortingHandler()}>
                                                {flexRender(
                                                    header.column.columnDef.header,
                                                    header.getContext()
                                                )}
                                            </div>
                                            {!showFilters ? null :(header.column.getCanFilter() && (
                                                <div>
                                                    <Filter column={header.column} />
                                                </div>) || <div className="fill-no-filter"></div>)}
                                        </>
                                    )}
                            </th>
                        ))}
                    </tr>
                ))}
                </thead>
                <tbody>
                {table.getRowModel().rows.map(row => (
                    <tr key={row.id}>
                        {row.getVisibleCells().map(cell => (
                            <td key={cell.id}>
                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </td>
                        ))}
                    </tr>
                ))}
                </tbody>
            </Table>
        </div>
    );
}

const getPageCount = (totalCount: number, pageSize: number): number => {
    return Math.ceil(totalCount/pageSize) || 0;
};

interface AllReactTableProps {
    columns: ColumnDef<MapType>[]
    commonStore: CommonStore
    mapType: Mapping
    showFilters: boolean
}
function AllReactTable({columns, commonStore, mapType, showFilters}: AllReactTableProps) {
    const [dataQuery, setDataQuery] = useState<MapTypeDataQuery | null>(null);
    const [sorting, setSorting] = useState<SortingState>([]);
    const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
    const [{ pageIndex, pageSize }, setPagination] =
        React.useState<PaginationState>({
            pageIndex: 0,
            pageSize: Defaults.tablePageSize(),
        });

    const pagination = React.useMemo(
        () => ({
            pageIndex,
            pageSize,
        }),
        [pageIndex, pageSize]
    );
    const table = useReactTable({
        columns,
        data: dataQuery?.data ?? [],
        pageCount: getPageCount(dataQuery?.result?.totalCount ?? 0, pageSize),
        getCoreRowModel: getCoreRowModel(),
        state: {
            pagination,
            sorting,
            columnFilters
        },
        onPaginationChange: setPagination,
        onSortingChange: setSorting,
        onColumnFiltersChange: setColumnFilters,
        manualPagination: true,
        manualSorting: true,
        manualFiltering: true,
    });

    useEffect(() => {
        async function fetchData() {
            let data: MapType[];
            let totalCount = 0;
            let sortBy;
            let sortOrder;
            if (sorting.length > 0) {
                sortBy = sorting[0].id;
                sortOrder = sorting[0].desc ? "desc" : "asc";
            }

            let filters;
            if (columnFilters.length > 0) {
                filters = {};
                columnFilters.forEach(filter => {
                    if (filter.id === "status") {
                        if (filter.value === "all") {
                            return;
                        } else if (filter.value === "active" || filter.value === "inactive") {
                            filters[filter.id] = { operator: "eq", value: filter.value};
                        } else {
                            // it means other
                            filters[filter.id] = [
                                { operator: "distinct", value: "active"},
                                { operator: "distinct", value: "inactive"}
                            ];
                        }
                    } else {
                        filters[filter.id] = { operator: "contains", value: filter.value};
                    }
                });
            }
            switch (mapType) {
                case "students":
                    await commonStore.fetchStudents({pageSize, page: pageIndex + 1, sortBy, sortOrder, filters});
                    data = commonStore[mapType] || [];
                    totalCount = commonStore.totalStudents;
                    break;
                case "courses":
                    await commonStore.fetchCoursesAsync("all", {pageSize, page: pageIndex + 1, sortBy, sortOrder, filters});
                    data = commonStore[mapType] || [];
                    totalCount = commonStore.totalCourses;
                    break;
                case "committees":
                    await commonStore.fetchCommittees();
                    data = toJS(commonStore["filteredSorted" + _.capitalize(mapType)]) || [];
                    break;
            }
            setDataQuery({ data, result: {totalCount}});
        }

        fetchData().catch(() => {});

    }, [pageIndex, sorting, columnFilters]);

    const getTotalRecords = () => {
        return dataQuery?.result.totalCount || 0;
    };
    const onNextPage = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        table.nextPage();
    };

    const onPreviousPage = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        table.previousPage();
    };
    const showRange = () => {
        if (getTotalRecords() === 0) return "";

        const current = (pageSize * (pageIndex + 1)) - pageSize + 1;
        const end = pageSize * (pageIndex + 1) > getTotalRecords()
            ? getTotalRecords()
            : pageSize * (pageIndex + 1);
        const total = getTotalRecords();

        return ` ${current} - ${end} of ${total} `;
    };

    return (
        <div className={"list"}>
            <Row className={"mb-2"}>
                <Col>
                    <div className="center">
                        <button className="back"
                                data-test-id="user-mapping-page-back"
                                onClick={onPreviousPage}
                                disabled={!table.getCanPreviousPage()}>
                            <i className="icon icon-angle-left"/>
                        </button>
                        <span className="range">{showRange()}</span>
                        <button onClick={onNextPage} className="forward"
                                data-test-id="user-mapping-page-forward"
                                disabled={!table.getCanNextPage() || Utils.isNextButtonDisabled(pageIndex, pageSize, getTotalRecords())}>
                            <i className="icon icon-angle-right"/>
                        </button>
                    </div>
                </Col>
            </Row>
            <div className={"table-container"}>
                <Table>
                    <thead>
                    {table.getHeaderGroups().map(headerGroup => (
                        <tr key={headerGroup.id}>
                            {headerGroup.headers.map(header => (
                                <th key={header.id}>
                                    {header.isPlaceholder
                                        ? null
                                        : (
                                            <>
                                                <div
                                                    className={header.column.getCanSort() ? "cursor-pointer " + (header.column.getIsSorted() || "") : ""}
                                                    onClick={header.column.getToggleSortingHandler()}>
                                                    {flexRender(
                                                        header.column.columnDef.header,
                                                        header.getContext()
                                                    )}
                                                </div>
                                                {!showFilters ? null :(header.column.getCanFilter() && (
                                                    <div>
                                                        <Filter column={header.column} />
                                                    </div>) || <div className="fill-no-filter"></div>)}
                                            </>
                                        )}
                                </th>
                            ))}
                        </tr>
                    ))}
                    </thead>
                    <tbody>
                    {table.getRowModel().rows.map(row => (
                        <tr key={row.id}>
                            {row.getVisibleCells().map(cell => (
                                <td key={cell.id}>
                                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                </td>
                            ))}
                        </tr>
                    ))}
                    </tbody>
                </Table>
            </div>
        </div>
    );
}

function Filter({column}: {column: Column<any, unknown>}) {
    const columnFilterValue = column.getFilterValue();

    if (column.id === "status") {
        return (
            <Form.Select onChange={e => column.setFilterValue(e.target.value)}>
                <option value="all">All</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="other">Other</option>
            </Form.Select>
        );
    }

    return (
        <DebouncedInput value={(columnFilterValue ?? "") as string} onChange={value => column.setFilterValue(value)} />
    );
}
